import { gql, useMutation } from "@apollo/client";

const MAX_FILES_PER_BATCH = 250; // more than this will block the Lambda, as the response will be too large

export const MUTATION_CREATE_UPLOAD_LINK = gql`
  mutation createUploadLinks(
    $absolutePaths: [String]!
    $md5sums: [String]!
    $activityId: ID!
    $actionFileTypes: [ActionFileType]
    $actionId: ID
    $qualityCheckStatus: QualityCheckStatus
    $uncompressOnUpload: Boolean
  ) {
    createUploadLinks(
      input: {
        activityId: $activityId
        md5sums: $md5sums
        absolutePaths: $absolutePaths
        actionFileTypes: $actionFileTypes
        actionId: $actionId
        qualityCheckStatus: $qualityCheckStatus
        uncompressOnUpload: $uncompressOnUpload
      }
    ) {
      actionId
      actionStatus
      presignedUrls {
        url
        fields
      }
    }
  }
`;

type CreateUploadLinksVariables = {
  absolutePaths: string[];
  md5sums: string[];
  activityId: Number;
  actionFileTypes?: any[];
  actionId?: Number;
  qualityCheckStatus?: any;
  uncompressOnUpload?: boolean;
};

export function useCreateUploadLinkMutation() {
  const [
    createUploadLinksMutation,
    { data: uploadData, loading: uploadLoading, error: uploadLinksError }
  ] = useMutation(MUTATION_CREATE_UPLOAD_LINK, {
    onError(err: any) {
      console.log(err);
    }
  });

  const getUploadLinksForFiles = async ({
    absolutePaths,
    md5sums,
    activityId,
    actionFileTypes,
    actionId,
    qualityCheckStatus,
    uncompressOnUpload
  }: CreateUploadLinksVariables) => {
    if (
      absolutePaths.length !== md5sums.length ||
      (actionFileTypes && actionFileTypes.length !== absolutePaths.length)
    ) {
      throw new Error(
        "absolutePaths, md5sums, and actionFileTypes must have the same length"
      );
    }

    const allPresignedUrls: Array<{ url: string; fields: any }> = [];
    let currentActionId = actionId;
    let lastActionStatus;

    for (let i = 0; i < absolutePaths.length; i += MAX_FILES_PER_BATCH) {
      const batchAbsolutePaths = absolutePaths.slice(
        i,
        i + MAX_FILES_PER_BATCH
      );
      const batchMd5sums = md5sums.slice(i, i + MAX_FILES_PER_BATCH);
      const batchActionFileTypes = actionFileTypes?.slice(
        i,
        i + MAX_FILES_PER_BATCH
      );

      const result = await createUploadLinksMutation({
        variables: {
          absolutePaths: batchAbsolutePaths,
          md5sums: batchMd5sums,
          activityId,
          actionFileTypes: batchActionFileTypes,
          actionId: currentActionId,
          qualityCheckStatus,
          uncompressOnUpload
        }
      });

      const batchResult = result?.data?.createUploadLinks;

      if (batchResult?.presignedUrls) {
        allPresignedUrls.push(...batchResult.presignedUrls);
      }

      if (batchResult?.actionId && !currentActionId) {
        currentActionId = batchResult.actionId;
      }

      lastActionStatus = batchResult?.actionStatus;
    }

    return {
      data: {
        createUploadLinks: {
          actionId: currentActionId,
          actionStatus: lastActionStatus,
          presignedUrls: allPresignedUrls
        }
      }
    };
  };

  return {
    getUploadLinksForFiles,
    uploadData,
    uploadLoading,
    uploadLinksError
  };
}
